import { Fragment, useEffect, useMemo, useRef, useState } from 'react'

import { formatHeld, type HeldSymbol } from '@/lib/positions'
import type { Instrument, LiveQuote } from '@/lib/types'

/**
 * Sélecteur de paire.
 *
 * Feuille coulissante depuis le bas sur téléphone — le motif natif iOS, la
 * liste s'ouvre là où se trouve le pouce — et fenêtre centrée sur ordinateur,
 * où une feuille collée en bas oblige à descendre la souris sur 800 pixels.
 *
 * Trois choses rendent le choix rapide :
 *
 * - **Le prix et le spread, en direct, sur chaque ligne.** Choisir une paire,
 *   c'est choisir où le marché bouge et ce que l'aller-retour coûte. Sans ces
 *   deux chiffres, il fallait ouvrir une paire pour la voir, puis une autre.
 *   Un spread anormalement large — ouverture, clôture, annonce — se repère
 *   alors avant d'avoir engagé quoi que ce soit.
 * - **Les dernières paires utilisées**, en haut : on tourne presque toujours
 *   sur les mêmes, et elles ne sont pas forcément en favori.
 * - **Le clavier** sur ordinateur : la recherche prend le focus, les flèches
 *   parcourent la liste, Entrée choisit, Échap referme. Sans quitter les
 *   touches, trois frappes suffisent (« jp », flèche, Entrée).
 *
 * La recherche filtre sur le nom canonique comme sur les deux devises, pour
 * qu'« eur » ou « jpy » remontent tout ce qui les concerne.
 *
 * Un symbole désactivé sur le compte est grisé et non sélectionnable — jamais
 * masqué : le voir absent d'une liste inquiète moins que de le voir grisé avec
 * une raison (§6).
 */
export function SymbolPicker({
  instruments,
  value,
  favourites,
  quotes,
  held = {},
  recents = [],
  onSelect,
  onToggleFavourite,
  onClose,
}: {
  instruments: Instrument[]
  value: string
  favourites: string[]
  quotes: Record<string, LiveQuote>
  /** Positions ouvertes par paire, marquées dans la liste. */
  held?: Record<string, HeldSymbol>
  /** Dernières paires choisies, la plus récente en tête. */
  recents?: string[]
  onSelect: (symbol: string) => void
  onToggleFavourite: (symbol: string) => void
  onClose: () => void
}) {
  const [search, setSearch] = useState('')
  const [tab, setTab] = useState<'all' | 'favourite'>('all')
  const [active, setActive] = useState(0)
  /*
   * Le rang parcouru n'est encadré que lorsqu'il sert : au clavier, ou quand
   * la recherche a le focus, puisqu'Entrée choisira cette ligne-là. Affiché en
   * permanence, ce cadre se lisait sur téléphone comme la paire déjà choisie.
   */
  const [keyboardNav, setKeyboardNav] = useState(false)
  const [searching, setSearching] = useState(false)
  const showCursor = keyboardNav || searching
  const searchField = useRef<HTMLInputElement>(null)
  const list = useRef<HTMLUListElement>(null)

  const visible = useMemo(() => {
    const query = search.trim().toUpperCase()
    if (query) {
      return instruments.filter(
        (item) =>
          item.name.includes(query) ||
          item.base_asset.includes(query) ||
          item.quote_asset.includes(query),
      )
    }
    if (tab === 'favourite') return instruments.filter((item) => favourites.includes(item.name))
    /*
     * Toutes les paires dans une seule liste, majeures d'abord.
     *
     * Elles étaient réparties sur deux onglets. Chercher une mineure demandait
     * donc de savoir d'avance qu'elle en était une — et de changer d'onglet
     * avant même de regarder. Un intertitre suffit à séparer les deux familles
     * sans couper la liste en deux.
     */
    return [
      ...instruments.filter((item) => item.category === 'major'),
      ...instruments.filter((item) => item.category === 'cross'),
    ]
  }, [instruments, search, tab, favourites])

  /** Vrai quand la ligne ouvre une famille : on y pose l'intertitre. */
  function sectionLabel(index: number): string | null {
    if (search || tab !== 'all') return null
    const item = visible[index]
    if (!item) return null
    if (index === 0) return item.category === 'major' ? 'Majeures' : 'Mineures'
    return visible[index - 1].category !== item.category
      ? item.category === 'major'
        ? 'Majeures'
        : 'Mineures'
      : null
  }

  // La sélection clavier repart du début dès que la liste change : la laisser
  // sur un rang devenu hors liste ferait choisir une autre paire que celle
  // qu'on voit en surbrillance.
  useEffect(() => {
    setActive(0)
  }, [search, tab])

  /*
   * Focus automatique sur la recherche, mais seulement au pointeur fin.
   * Sur un téléphone, il ferait monter le clavier par-dessus la moitié de la
   * liste, juste pour choisir parmi quelques paires visibles.
   */
  useEffect(() => {
    if (window.matchMedia('(pointer: fine)').matches) searchField.current?.focus()
  }, [])

  // La ligne parcourue au clavier doit rester visible, sans faire sauter la
  // page entière : `nearest` déplace du minimum nécessaire.
  useEffect(() => {
    list.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [active])

  function choose(symbol: string) {
    onSelect(symbol)
    onClose()
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
      return
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      if (visible.length === 0) return
      setKeyboardNav(true)
      const step = event.key === 'ArrowDown' ? 1 : -1
      setActive((current) => Math.min(Math.max(current + step, 0), visible.length - 1))
      return
    }
    if (event.key === 'Enter') {
      const item = visible[active]
      if (item && item.enabled) {
        event.preventDefault()
        choose(item.name)
      }
    }
  }

  const recentlyUsed = recents.filter((name) => name !== value).slice(0, 6)

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col justify-end sm:items-center sm:justify-center sm:p-4"
      onKeyDown={onKeyDown}
    >
      {/* Voile : un appui en dehors de la feuille la referme. */}
      <button
        type="button"
        aria-label="Fermer"
        onClick={onClose}
        className="absolute inset-0 bg-black/60"
      />

      <div
        role="dialog"
        aria-label="Choisir une paire"
        className="safe-bottom relative flex max-h-[80vh] w-full flex-col rounded-t-2xl border-t border-border bg-panel sm:max-h-[32rem] sm:max-w-md sm:rounded-2xl sm:border"
      >
        {/* Poignée visuelle : indique que la feuille se referme vers le bas.
            Inutile sur ordinateur, où la fenêtre est centrée. */}
        <div className="flex justify-center py-2 sm:hidden">
          <span className="h-1 w-10 rounded-full bg-border" />
        </div>

        <div className="px-3 pb-2 pt-0 sm:pt-3">
          <input
            ref={searchField}
            type="search"
            inputMode="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onFocus={() => setSearching(true)}
            onBlur={() => setSearching(false)}
            placeholder="Rechercher (EUR, JPY, GBPUSD…)"
            className="min-h-touch w-full rounded-md border border-border bg-elevated px-3 text-ink outline-none placeholder:text-ink-muted/60 focus:border-accent"
          />
        </div>

        {!search && recentlyUsed.length > 0 && (
          <div className="flex gap-1 overflow-x-auto px-3 pb-2 [scrollbar-width:none]">
            <span className="self-center pr-1 text-[10px] uppercase tracking-wide text-ink-muted">
              Récentes
            </span>
            {recentlyUsed.map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => choose(name)}
                className="num min-h-touch shrink-0 rounded bg-elevated px-2.5 text-[12px] font-semibold text-ink-muted hover:bg-border/60 active:bg-border"
              >
                {name}
              </button>
            ))}
          </div>
        )}

        {!search && (
          <div className="flex gap-1 px-3 pb-2">
            {(
              [
                { key: 'all', label: 'Paires de devises' },
                { key: 'favourite', label: 'Favoris' },
              ] as const
            ).map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setTab(item.key)}
                className={`min-h-touch flex-1 rounded-md px-2 text-[12px] font-semibold ${
                  tab === item.key
                    ? 'bg-accent text-white'
                    : 'bg-elevated text-ink-muted hover:bg-border/60 active:bg-border'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}

        <ul ref={list} className="min-h-0 flex-1 overflow-y-auto border-t border-border">
          {visible.length === 0 && (
            <li className="px-3 py-6 text-center text-[13px] text-ink-muted">
              {tab === 'favourite' && !search
                ? 'Aucun favori. Appuyer sur ★ pour en ajouter.'
                : 'Aucune paire ne correspond.'}
            </li>
          )}

          {visible.map((item, index) => {
            const selected = item.name === value
            const quote = quotes[item.name]
            const pipSize = Number(item.pip_size)
            const spreadPips =
              quote && pipSize > 0 ? Number(quote.spread) / pipSize : null
            const famille = sectionLabel(index)

            return (
              <Fragment key={item.name}>
                {famille && (
                  /* `sticky` : en défilant, l'intertitre de la famille en
                     cours reste en haut de la liste. On sait toujours où
                     l'on se trouve sans remonter. */
                  <li className="sticky top-0 z-10 border-b border-border bg-panel px-3 py-1 text-[10px] uppercase tracking-wide text-ink-muted">
                    {famille}
                  </li>
                )}
              <li
                data-active={index === active ? 'true' : undefined}
                className={`flex items-stretch border-b border-border ${
                  showCursor && index === active ? 'ring-1 ring-inset ring-accent/60' : ''
                }`}
              >
                <button
                  type="button"
                  disabled={!item.enabled}
                  onClick={() => choose(item.name)}
                  onMouseEnter={() => {
                    setActive(index)
                    setKeyboardNav(false)
                  }}
                  className={`flex min-h-touch flex-1 items-center gap-2 px-3 text-left ${
                    selected ? 'bg-accent/15' : 'hover:bg-elevated/70 active:bg-elevated'
                  } ${item.enabled ? '' : 'opacity-40'}`}
                >
                  <span className="num flex-1 text-[15px] text-ink">
                    {item.base_asset}
                    <span className="text-ink-muted">/</span>
                    {item.quote_asset}
                    {/* Déjà engagé sur cette paire : le dire ICI, au moment du
                        choix, et avec le résultat latent — pas seulement dans
                        l'onglet des positions. */}
                    {held[item.name] && (
                      <span
                        className={`ml-2 whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                          held[item.name].profit >= 0
                            ? 'bg-up/15 text-up'
                            : 'bg-down/15 text-down'
                        }`}
                      >
                        {held[item.name].count > 1 && `${held[item.name].count}× `}
                        {formatHeld(held[item.name])}
                      </span>
                    )}
                  </span>

                  {item.enabled ? (
                    <>
                      <span className="num text-[13px] text-ink-strong">
                        {quote ? Number(quote.mid).toFixed(item.digits) : '—'}
                      </span>
                      {/* Le spread, en pips : c'est le coût immédiat de
                          l'aller-retour, et il explose à l'ouverture comme à
                          la clôture. */}
                      <span className="num w-14 text-right text-[11px] text-ink-muted">
                        {spreadPips === null ? '' : `${spreadPips.toFixed(1)} p`}
                      </span>
                    </>
                  ) : (
                    <span className="text-[11px] text-ink-muted">indisponible sur ce compte</span>
                  )}
                </button>
                <button
                  type="button"
                  aria-label={`Favori ${item.name}`}
                  aria-pressed={favourites.includes(item.name)}
                  onClick={() => onToggleFavourite(item.name)}
                  className={`min-h-touch w-11 shrink-0 text-[16px] ${
                    favourites.includes(item.name) ? 'text-warn' : 'text-ink-muted/40'
                  }`}
                >
                  ★
                </button>
              </li>
              </Fragment>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
